// §18 Marketing (layout 3, 2026-09-24). Worked through together by
// Marketing (the section's default owner), the salesperson and the PM. Four
// parts, each with a suggested lead and its own "done" sign-off; the section
// completes when all four are signed off (getSectionMissing → kickoffMissing).
// Autosaves to TurnoverMarketingKickoff (sp_SaveMarketingKickoff). The Client
// confidentiality answers live on the Cover Sheet and are shown here read-only.
// Rules and catalogs: src/config/marketingKickoff.js.
import { useEffect, useRef, useState } from "react";
import { turnoverWrite, payloadOf } from "../../utils/sqlClient";
import { hubspotProxy } from "../../utils/hubspotSync";
import {
  YES_NO_TBD, KICKOFF_PARTS, PUBLIC_REFERENCE_LABEL,
  normalizeKickoff, partMissing, kickoffProgress, kickoffPayload, kickoffDates, effectiveDescription, isoDate,
} from "../../config/marketingKickoff";
import { checklistItems, selectChoices } from "../../config/turnoverOptions";

const card = { border: "1px solid var(--cs-border)", borderRadius: 10, background: "var(--surface)", marginBottom: 14, overflow: "hidden" };
const headBar = { padding: "10px 16px", borderBottom: "1px solid var(--cs-border)", background: "#f8f9fb", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" };
const body = { padding: "12px 16px" };
const lbl = { fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 };
const ctl = { width: "100%", padding: "7px 10px", fontSize: 13, border: "1px solid var(--cs-border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)", boxSizing: "border-box", fontFamily: "inherit" };
const area = { ...ctl, resize: "vertical", minHeight: 0 };
const grid = (cols) => ({ display: "grid", gridTemplateColumns: cols, gap: "12px 18px", marginBottom: 12 });
const fmt = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");
const stamp = (at) => (at ? new Date(at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "");

export function Choices({ options, value, onChange, disabled }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {options.map(o => {
        const on = value === o.key;
        return (
          <button key={o.key} type="button" disabled={disabled} onClick={() => onChange(on ? "" : o.key)} style={{
            padding: "5px 12px", fontSize: 12.5, fontWeight: 600, borderRadius: 100, cursor: disabled ? "default" : "pointer",
            border: `1px solid ${on ? "#93c5fd" : "var(--cs-border)"}`, background: on ? "#dbeafe" : "var(--surface)", color: on ? "#1e40af" : "var(--text2)",
          }}>{o.label}</button>
        );
      })}
    </div>
  );
}

function Checks({ items, values, onToggle, disabled, cols = "repeat(auto-fill, minmax(280px, 1fr))" }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: cols, gap: "6px 18px" }}>
      {items.map(i => (
        <label key={i.key} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, color: "var(--text)", cursor: disabled || i.required ? "default" : "pointer" }}>
          <input type="checkbox" checked={!!values[i.key] || !!i.required} disabled={disabled || i.required} onChange={() => onToggle(i.key)} />
          <span>{i.label}{i.required && <strong style={{ color: "#b45309" }}> (required)</strong>}</span>
        </label>
      ))}
    </div>
  );
}

export default function MarketingKickoffPanel({ turnover, schedule = [], data: stored, onChange, canEdit, user, optionRows = [], canScheduleMeeting = false, onScheduleMeeting }) {
  const data = normalizeKickoff(stored);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error
  const [closeDate, setCloseDate] = useState(null);
  const timer = useRef(null);
  const latest = useRef(data);
  latest.current = data;
  const saveRef = useRef(null);

  useEffect(() => {
    let alive = true;
    if (turnover?.HubSpotDealID) {
      hubspotProxy(`https://api.hubapi.com/crm/v3/objects/deals/${turnover.HubSpotDealID}?properties=closedate`)
        .then(r => alive && setCloseDate(r?.properties?.closedate || null)).catch(() => {});
    }
    return () => { alive = false; };
  }, [turnover?.HubSpotDealID]);
  // Leaving the section with a save pending: save it now rather than drop it.
  useEffect(() => () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; saveRef.current?.(); }
  }, []);

  async function save() {
    timer.current = null;
    setSaveState("saving");
    try {
      const res = await turnoverWrite(`EXEC sp_SaveMarketingKickoff @payload = '${payloadOf(kickoffPayload(turnover.TurnoverID, latest.current, user))}'`);
      const r = res?.ResultSets?.Table1?.[0];
      if (!r || r.Result !== 1) throw new Error(r?.Error || "Not saved.");
      setSaveState("saved");
    } catch (e) {
      setSaveState("error");
      console.error("[Marketing] save failed:", e);
    }
  }
  saveRef.current = save;

  function set(patch) {
    if (!canEdit) return;
    const next = normalizeKickoff({ ...latest.current, ...patch });
    latest.current = next;
    onChange(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(save, 800);
  }
  const toggle = (field, key) => set({ [field]: { ...data[field], [key]: !data[field][key] } });
  const text = (key, rows, placeholder) => (
    rows
      ? <textarea rows={rows} value={data[key]} disabled={!canEdit} placeholder={placeholder} onChange={e => set({ [key]: e.target.value })} style={area} />
      : <input value={data[key]} disabled={!canEdit} placeholder={placeholder} onChange={e => set({ [key]: e.target.value })} style={ctl} />
  );

  const ctx = { schedule, turnover };
  const dates = kickoffDates(data, schedule);
  const progress = kickoffProgress(data);
  const decalChoices = selectChoices("mk_decal_types", optionRows, data.decalType);
  const description = effectiveDescription(data, turnover);

  // A plain render function (not a component) so the cards' inputs keep focus.
  function renderPart(part, children) {
    const need = partMissing(part.key, data, ctx);
    const done = data.partsDone[part.key];
    const markDone = () => set({ partsDone: { ...data.partsDone, [part.key]: { by: user?.name || "", at: new Date().toISOString() } } });
    const undo = () => { const p = { ...data.partsDone }; delete p[part.key]; set({ partsDone: p }); };
    return (
      <div style={card}>
        <div style={headBar}>
          <span style={{ fontSize: 14, fontWeight: 700, color: "var(--text)" }}>{done ? "✅" : "⬜"} {part.label}</span>
          <span style={{ fontSize: 11.5, color: "var(--text3)" }}>Lead: {part.lead}</span>
          <span style={{ marginLeft: "auto", fontSize: 12 }}>
            {done ? (
              <>
                <span style={{ color: "#15803d", fontWeight: 600 }}>Done{done.by ? ` — ${done.by}` : ""}{done.at ? `, ${stamp(done.at)}` : ""}</span>
                {canEdit && <button type="button" onClick={undo} style={{ marginLeft: 8, fontSize: 11.5, color: "var(--text3)", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>Undo</button>}
              </>
            ) : canEdit && (
              <button type="button" disabled={need.length > 0} onClick={markDone} title={need.length ? `Still needed: ${need.join("; ")}` : ""}
                style={{ padding: "4px 12px", fontSize: 12, fontWeight: 700, borderRadius: 6, cursor: need.length ? "not-allowed" : "pointer",
                  border: "1px solid #86efac", background: need.length ? "var(--surface)" : "#dcfce7", color: need.length ? "var(--text3)" : "#166534" }}>
                ✓ Mark done
              </button>
            )}
          </span>
        </div>
        <div style={body}>
          {!done && need.length > 0 && <div style={{ fontSize: 11.5, color: "#b45309", marginBottom: 8 }}>Still needed: {need.join(" · ")}</div>}
          {children}
        </div>
      </div>
    );
  }

  const [signage, content, sheet, milestones] = KICKOFF_PARTS;
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 10, fontSize: 12.5, color: "var(--text2)" }}>
        <span>Marketing, the salesperson and the Project Manager work through this together — each part names its lead. Changes save automatically.</span>
        <span style={{ whiteSpace: "nowrap", color: "var(--text3)" }}>
          <strong style={{ color: progress.done === progress.total ? "#15803d" : "var(--text2)" }}>{progress.done} / {progress.total} parts done</strong>
          {" · "}{saveState === "saving" ? "Saving…" : saveState === "error" ? <span style={{ color: "#b91c1c" }}>Not saved — check your connection</span> : saveState === "saved" ? "Saved" : ""}
          {!canEdit && <span style={{ color: "#b45309" }}> View only</span>}
        </span>
      </div>

      {renderPart(signage, <>
        <div style={lbl}>Signage required for this project?</div>
        <Choices options={YES_NO_TBD} value={data.signageRequired} onChange={v => set({ signageRequired: v })} disabled={!canEdit} />
        {data.signageRequired !== "no" && [["branding", "Site branding"], ["construction", "Construction & safety"], ["marketing", "Marketing"]].map(([k, label]) => (
          <div key={k} style={{ marginTop: 12 }}>
            <div style={{ ...lbl, marginBottom: 6 }}>{label} — select all that apply</div>
            <Checks items={checklistItems(`mk_signage_${k}`, optionRows, data.signs)} values={data.signs} onToggle={key => toggle("signs", key)} disabled={!canEdit} />
          </div>
        ))}
        <div style={{ marginTop: 14, ...lbl }}>Hard hat decals required?</div>
        <Choices options={YES_NO_TBD} value={data.decalsRequired} onChange={v => set({ decalsRequired: v })} disabled={!canEdit} />
        {data.decalsRequired === "yes" && (
          <div style={{ ...grid("2fr 1fr 1fr"), marginTop: 12 }}>
            <div><div style={lbl}>Decal type</div>{decalChoices.length
              ? <select value={data.decalType} disabled={!canEdit} onChange={e => set({ decalType: e.target.value })} style={ctl}>
                  <option value="">— Select —</option>
                  {decalChoices.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              : text("decalType")}</div>
            <div><div style={lbl}>Estimated # of workers</div>
              <input type="number" min="0" value={data.estimatedWorkers} disabled={!canEdit} onChange={e => set({ estimatedWorkers: e.target.value })} style={ctl} /></div>
            <div><div style={lbl}>Numbered? (starts at 001)</div>
              <Choices options={YES_NO_TBD.slice(0, 2)} value={data.decalsNumbered} onChange={v => set({ decalsNumbered: v })} disabled={!canEdit} /></div>
          </div>
        )}
        <div style={{ marginTop: 12 }}><div style={lbl}>Signage notes / special instructions</div>{text("signageNotes", 2)}</div>
      </>)}

      {renderPart(content, <>
        <div style={{ fontSize: 12.5, color: "var(--text2)", background: "#f8f9fb", border: "1px solid var(--cs-border)", borderRadius: 6, padding: "8px 10px", marginBottom: 12 }}>
          <strong>Client confidentiality</strong> (from the Cover Sheet): {PUBLIC_REFERENCE_LABEL[turnover?.PublicReference] || <span style={{ color: "#b45309" }}>not answered yet</span>}
          {turnover?.ConfidentialityNotes && <div style={{ marginTop: 4 }}>Notes: {turnover.ConfidentialityNotes}</div>}
          {turnover?.PhotographyRestrictions && <div style={{ marginTop: 4 }}>Photography limits: {turnover.PhotographyRestrictions}</div>}
        </div>
        <div style={{ ...lbl, marginBottom: 6 }}>Opportunities identified — select all that apply</div>
        <Checks items={checklistItems("mk_content", optionRows, data.opportunities)} values={data.opportunities} onToggle={key => toggle("opportunities", key)} disabled={!canEdit} cols="repeat(auto-fill, minmax(220px, 1fr))" />
        <div style={{ ...grid("1fr 2fr"), marginTop: 12 }}>
          <div><div style={lbl}>Photography budget approved?</div>
            <Choices options={YES_NO_TBD} value={data.photographyBudgetApproved} onChange={v => set({ photographyBudgetApproved: v })} disabled={!canEdit} /></div>
          <div><div style={lbl}>Content notes (client approval, story angles, award categories, …)</div>{text("contentNotes", 2)}</div>
        </div>
      </>)}

      {renderPart(sheet, <>
        <div style={{ fontSize: 12.5, color: "var(--text2)", marginBottom: 10 }}>
          Market <strong>{turnover?.Market || "—"}</strong> · Square footage <strong>{Number(turnover?.SquareFeet) ? `${Number(turnover.SquareFeet).toLocaleString("en-US")} SF` : "—"}</strong> · Award date <strong>{closeDate ? fmt(isoDate(closeDate)) : "—"}</strong> <span style={{ color: "var(--text3)" }}>(HubSpot Closed Won)</span>
        </div>
        <div style={lbl}>Project description (scope, size, key features)</div>
        <textarea rows={3} value={description} disabled={!canEdit} onChange={e => set({ projectDescription: e.target.value })} style={area} />
        {!String(data.projectDescription || "").trim() && description && <div style={{ fontSize: 10.5, color: "var(--text3)", marginTop: 2 }}>From the HubSpot deal description — edit as needed</div>}
        <div style={{ marginTop: 12 }}><div style={lbl}>Notable project features to highlight</div>{text("notableFeatures", 2)}</div>
        <div style={{ marginTop: 12, ...lbl }}>Existing project imagery available? (renderings, existing photos)</div>
        <Choices options={YES_NO_TBD} value={data.imageryAvailable} onChange={v => set({ imageryAvailable: v })} disabled={!canEdit} />
      </>)}

      {renderPart(milestones, <>
        <div style={grid("1fr 1fr")}>
          {[["start", "estimatedStart", "Estimated start", dates.start, dates.startFrom], ["completion", "estimatedCompletion", "Estimated completion", dates.completion, dates.completionFrom]].map(([k, field, label, value, from]) => (
            <div key={k}>
              <div style={lbl}>{label}</div>
              {from ? <div style={{ fontSize: 13 }}>{fmt(value)} <span style={{ fontSize: 11, color: "var(--text3)" }}>— §9 {from}</span></div>
                : <><input type="date" value={data[field]} disabled={!canEdit} onChange={e => set({ [field]: e.target.value })} style={ctl} />
                    <div style={{ fontSize: 10.5, color: "var(--text3)", marginTop: 2 }}>No dated milestone on §9 yet</div></>}
            </div>
          ))}
        </div>
        <div style={{ ...lbl, marginBottom: 6 }}>§9 milestones — tick the photo / content opportunities</div>
        {dates.milestones.length === 0
          ? <div style={{ fontSize: 12.5, color: "var(--text3)" }}>No dated milestones on §9 Project Schedule yet.</div>
          : <Checks items={dates.milestones.map(m => ({ key: String(m.id ?? m.label), label: `📷 ${m.label} — ${fmt(m.date)}` }))}
              values={data.photoMilestones} onToggle={key => toggle("photoMilestones", key)} disabled={!canEdit} />}
      </>)}

      <div style={card}>
        <div style={headBar}><span style={{ fontSize: 14, fontWeight: 700, color: "var(--text)" }}>Turnover meeting — includes Marketing</span>
          <span style={{ fontSize: 11.5, color: "var(--text3)" }}>One meeting: the package walkthrough, then this section. Marketing is invited automatically.</span></div>
        <div style={body}>
          {turnover?.TurnoverMeetingAt ? (
            <div style={{ fontSize: 13, marginBottom: 10 }}>
              📅 <strong>{new Date(String(turnover.TurnoverMeetingAt).slice(0, 19)).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}</strong> ET{turnover.TurnoverMeetingLocation ? ` · ${turnover.TurnoverMeetingLocation}` : ""}
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", background: "#f5f3ff", border: "1px solid #c4b5fd", borderRadius: 8, padding: "10px 14px", marginBottom: 12 }}>
              <div style={{ flex: "1 1 320px", fontSize: 13, color: "#4c1d95" }}>
                <strong>Schedule the turnover meeting.</strong>{" "}
                {canScheduleMeeting
                  ? "Print Package, then Email Package: set the date and time and one invite goes to the team, the PM and Marketing."
                  : "Available once every section, including this one, is complete — the salesperson then sends the package with the invite."}
              </div>
              {onScheduleMeeting && (
                <button type="button" disabled={!canScheduleMeeting} onClick={onScheduleMeeting}
                  style={{ padding: "7px 16px", fontSize: 13, fontWeight: 700, borderRadius: 6, border: "none", whiteSpace: "nowrap",
                    background: canScheduleMeeting ? "#5b21b6" : "#ddd6fe", color: "#fff", cursor: canScheduleMeeting ? "pointer" : "not-allowed" }}>
                  📅 Schedule the meeting
                </button>
              )}
            </div>
          )}
          <div style={lbl}>Marketing follow-ups from the turnover meeting (owner and due date)</div>
          {text("followUps", 2)}
        </div>
      </div>
    </div>
  );
}
